import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import SetupDiagram from "@/components/activity/SetupDiagram";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

// Highlight [PLACEHOLDER] tokens (kept subtle for print legibility).
function withPlaceholders(text) {
  return text.split(/(\[[^\]]+\])/g).map((part, i) =>
    /^\[[^\]]+\]$/.test(part) ? (
      <strong key={i} className="text-brand-orange">{part}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function TickRow({ children }) {
  const lines = String(children).split("\n");
  const header = lines[0];
  const bullets = lines.slice(1).filter((l) => l.trim() !== "");
  return (
    <li className="flex items-start gap-2.5 print-avoid py-1">
      <span className="mt-0.5 flex-shrink-0 w-4 h-4 border-2 border-brand-navy/60 rounded-[4px]" aria-hidden="true" />
      <span className="text-[13px] leading-snug text-brand-navy">
        {header}
        {bullets.length > 0 && (
          <ul className="mt-1 pl-3 list-disc space-y-0.5">
            {bullets.map((b, bi) => (
              <li key={bi}>{b}</li>
            ))}
          </ul>
        )}
      </span>
    </li>
  );
}

function PhaseSection({ phase, index }) {
  return (
    <section className="print-avoid mb-6 border border-brand-navy/15 rounded-xl overflow-hidden">
      <div className="bg-brand-navy text-white px-4 py-2.5 flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-brand-orange text-white text-xs font-bold flex items-center justify-center">
          {index + 1}
        </span>
        <h3 className="display-title text-lg">{phase.name}</h3>
      </div>
      <div className="p-4 space-y-3">
        {phase.intro && <p className="text-[13px] text-brand-navy/80 leading-snug">{phase.intro}</p>}

        {phase.checklist?.length > 0 && (
          <ul className="space-y-0.5">
            {phase.checklist.map((item, i) => (
              <TickRow key={i}>{item}</TickRow>
            ))}
          </ul>
        )}

        {phase.diagram && (
          <div className="print-avoid">
            <SetupDiagram diagram={phase.diagram} />
          </div>
        )}

        {phase.cards?.length > 0 && (
          <ol className="space-y-2.5">
            {phase.cards.map((c) => (
              <li key={c.number} className="print-avoid border border-brand-navy/10 rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-6 h-6 rounded-md bg-brand-orange text-white font-display text-sm flex items-center justify-center">
                    {c.number}
                  </span>
                  <span className="display-title text-base text-brand-navy">{c.title}</span>
                  {c.duration && (
                    <span className="text-[11px] font-semibold text-brand-navy/60 bg-brand-navy/5 rounded-full px-2 py-0.5">
                      {c.duration}
                    </span>
                  )}
                </div>
                {c.body && <p className="mt-1 text-[13px] text-brand-navy/80 leading-snug">{c.body}</p>}
                {c.say && (
                  <p className="mt-1.5 text-[13px] italic text-brand-navy border-l-4 border-brand-orange pl-2.5">
                    <span className="not-italic font-bold text-brand-orange text-[10px] uppercase tracking-wide block">Say this</span>
                    &ldquo;{c.say}&rdquo;
                  </p>
                )}
                {c.subSteps?.length > 0 && (
                  <ol className="mt-2 space-y-1 list-decimal list-inside">
                    {c.subSteps.map((s, i) => (
                      <li key={i} className="text-[12.5px] text-brand-navy leading-snug">{s}</li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ol>
        )}

        {phase.tips?.length > 0 &&
          phase.tips.map((t, i) => (
            <p key={i} className="text-[12.5px] text-brand-navy bg-brand-cream border border-brand-cream-dark rounded-lg px-3 py-2">
              <span className="font-bold text-brand-orange">Tip: </span>
              {t}
            </p>
          ))}

        {phase.promotionTemplates?.length > 0 && (
          <div className="space-y-2.5">
            {phase.promotionTemplates.map((tpl) => (
              <div key={tpl.id} className="print-avoid border border-brand-navy/10 rounded-lg p-3">
                <p className="font-semibold text-brand-navy text-sm mb-1">{tpl.title}</p>
                {tpl.subject && (
                  <p className="text-[13px] mb-1">
                    <span className="font-semibold text-brand-navy/60">Subject: </span>
                    {withPlaceholders(tpl.subject)}
                  </p>
                )}
                <p className="text-[13px] text-brand-navy leading-snug whitespace-pre-wrap">
                  {withPlaceholders(tpl.body)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function PrintableGuide({ mode }) {
  const { slug } = useParams();
  const { activity, status } = useActivity(slug);
  const modeData = activity?.modes?.[mode];
  useSeo(
    modeData ? `Print — ${modeData.name}` : "Printable guide",
    "A printable version of your Organizer guide."
  );

  // Nudge the print dialog open shortly after load for a true one-tap flow.
  useEffect(() => {
    if (status === "ok" && modeData) {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [status, modeData]);

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity) return <ErrorState onRetry={() => window.location.reload()} />;
  if (!modeData) return <NotFoundState />;

  return (
    <div className="min-h-screen bg-white print-color">
      {/* On-screen controls (hidden when printing) */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-brand-navy/10">
        <div className="mx-auto max-w-3xl px-4 py-3 flex items-center justify-between gap-3">
          <Link
            to={`/activities/${slug}/organizer`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy/70 hover:text-brand-orange"
          >
            <ArrowLeft className="w-4 h-4" /> Back to guide
          </Link>
          <button
            data-testid="do-print"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[44px] px-5 text-sm transition-colors"
          >
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Printable document */}
      <div className="mx-auto max-w-3xl px-6 py-6 print:px-0 print:py-0" data-testid="printable-doc">
        <header className="border-b-4 border-brand-orange pb-4 mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-brand-orange">Project in a Box · Organizer Guide</p>
          <h1 className="display-title text-3xl text-brand-navy mt-1">{activity.name}</h1>
          <p className="display-title text-xl text-brand-orange">{modeData.name}</p>
          <p className="text-[13px] text-brand-navy/70 mt-2 max-w-xl">{modeData.tagline}</p>
        </header>

        {/* Organizer Kit quick reference */}
        <section className="print-avoid mb-6">
          <h2 className="display-title text-lg text-brand-navy mb-2">What's in your Organizer Kit</h2>
          <div className="grid grid-cols-3 gap-2">
            {activity.ambassadorKit.contents.map((c, i) => (
              <div key={i} className="border border-brand-navy/10 rounded-lg p-2.5">
                <p className="text-[12.5px] font-semibold text-brand-navy">{c.title}</p>
                <p className="text-[11px] text-brand-navy/70 leading-snug mt-0.5">{c.description}</p>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-brand-navy mt-2">
            <span className="font-bold text-brand-orange">Remember: </span>
            {activity.ambassadorKit.callout}
          </p>
        </section>

        {modeData.phases.map((phase, i) => (
          <PhaseSection key={phase.key} phase={phase} index={i} />
        ))}

        <footer className="mt-4 pt-3 border-t border-brand-navy/15 text-center">
          <p className="text-[11px] text-brand-navy/50">© Project in a Box · Everything you need is in the box.</p>
        </footer>
      </div>
    </div>
  );
}
